/** Rossini's finale theme, in C for this four-voice arrangement. The opening
 * pickup and bars 1-16 follow the public-domain melody without omitted notes.
 * Source audit: docs/ORBIT_MUSIC_BOX_V3.md. */
export type InstrumentId = "brass" | "strings" | "bass" | "percussion";
export type Note = Readonly<{
  id: string;
  instrumentId: InstrumentId;
  startBeat: number;
  durationBeats: number;
  midi: number;
  velocity: number;
  articulation: "normal" | "kick" | "snare" | "hat";
  time: number;
  end: number;
  duration: number;
  finale: boolean;
  returnHit: boolean;
}>;
export const BPM = 160;
export const DURATION = 18;
export const MUSIC_END = 16.5;
export const BREAK_START = 17; // 6.375 seconds, after eight full 2/4 bars and the pickup
export const RETURN_BEAT = 18; // 6.75 seconds
export const FINAL_BEAT = 42; // 15.75 seconds
export const beatTime = (beat: number) => (beat * 60) / BPM;
export const timeBeat = (time: number) => (time * BPM) / 60;
export const BREAK = beatTime(BREAK_START);
export const RETURN = beatTime(RETURN_BEAT);
export const CLIMAX = beatTime(FINAL_BEAT);
export const ENTRIES: Record<InstrumentId, number> = {
  brass: 0, strings: 4, bass: 8, percussion: 12,
};

// Quarter note = one beat. Transposed melody: G -> C/D/E, no changed rhythms.
// Pickup + original bars 1-8; bars 9-16; then original closing gesture.
export const OPENING: readonly (readonly [number, number])[] = [
  [67,.5],[67,.5],
  [67,1],[67,.5],[67,.5], [67,1],[67,.5],[67,.5],
  [72,1],[74,1], [76,1],[67,.5],[67,.5],
  [67,1],[67,.5],[67,.5], [72,1],[76,.5],[76,.5],
  [74,1],[71,1], [67,1],[67,.5],[67,.5],
];
export const SECOND: readonly (readonly [number, number])[] = [
  [67,1],[67,.5],[67,.5], [67,1],[67,.5],[67,.5],
  [72,1],[74,1], [76,1],[72,.5],[76,.5],
  [79,2], [79,.5],[77,.5],[76,.5],[74,.5],
  [72,1],[76,1], [72,1],[76,.5],[76,.5],
];
const notes: Note[] = [];
const add = (instrumentId: InstrumentId, startBeat: number, midi: number,
  durationBeats: number, velocity: number,
  articulation: Note["articulation"] = "normal", finale = false,
  returnHit = false) => {
  const time = beatTime(startBeat), end = beatTime(startBeat + durationBeats);
  notes.push({ id: `v3-${instrumentId}-${notes.length}`, instrumentId,
    startBeat, midi, durationBeats, velocity, articulation,
    time, end, duration: end-time, finale, returnHit });
};
let beat = 0;
for (const [midi, length] of OPENING) {
  const accent=beat%2===0 ? .11 : beat%1===0 ? .065 : 0;
  add("brass", beat, midi, Math.min(length*.84,1.6),
    .68+accent+(length>=1 ? .045 : 0)+(beat>=12 ? .035 : 0));
  beat += length;
}
if (beat !== BREAK_START) throw Error(`Opening length ${beat}`);
beat = RETURN_BEAT;
for (const [midi, length] of SECOND) {
  const accent=beat%2===0 ? .085 : beat%1===0 ? .045 : 0;
  add("brass", beat, midi + (beat >= 26 && beat < 30 ? 12 : 0),
    Math.min(length*.88,1.7), beat===RETURN_BEAT ? 1 : .8+accent,
    "normal", false, beat === RETURN_BEAT);
  beat += length;
}
if (beat !== 34) throw Error(`Second phrase length ${beat}`);
// Original closing descent and tonic-third gesture (bars 37-39), with an
// inserted dominant bar before the original final C. This makes an actual V-I.
for (const [midi,length] of [
  [79,2],[79,.5],[77,.5],[76,.5],[74,.5],[72,1],[76,1],
  [74,.5],[71,.5],[67,1],
] as const) {
  const accent=beat%2===0 ? .08 : beat%1===0 ? .04 : 0;
  add("brass", beat, midi, Math.min(length*.9,1.6),
    .79+accent+(beat>=38 ? .08 : 0));
  beat += length;
}
if (beat !== FINAL_BEAT) throw Error(`Cadence length ${beat}`);

const harmony = (b: number) => b >= 40 ? [55,62,67] :
  b >= 38 ? [48,55,64] : b >= 34 ? [48,55,64] :
  b >= 30 ? [48,55,64] : b >= 26 ? [43,50,59] :
  b >= 22 ? [48,55,64] : b >= 18 ? [48,55,64] :
  b >= 14 ? [43,50,59] : [48,55,64];
// Tight string offbeats, sparse at first; after the return, switch between
// answering accents and fast bow strokes to keep the middle articulated.
for (let b=4; b<FINAL_BEAT; b+=.5) {
  if (b>=BREAK_START && b<RETURN_BEAT) continue;
  if (b<RETURN_BEAT && b%1===0) continue;
  if (b>=RETURN_BEAT && b<26 && b%1===0) continue;
  if (b>=30 && b<34 && b%1===.5) continue;
  const chord = harmony(b);
  add("strings", b, chord[Math.floor(b*2)%3]+12,
    b>=RETURN_BEAT ? .32 : .27,
    b===RETURN_BEAT ? .99 : b>=34 ? .65 : .45,
    "normal", false, b===RETURN_BEAT);
}
// Explicit common attack on the return, independent of the offbeat pattern.
add("strings", RETURN_BEAT, 76, .75, 1, "normal", false, true);
for (let b=8; b<FINAL_BEAT; b+=1) {
  if (b>=BREAK_START && b<RETURN_BEAT) continue;
  if (b<RETURN_BEAT && b%2!==0) continue;
  const root = harmony(b)[0];
  add("bass", b, root-12, b>=RETURN_BEAT ? .72 : 1.1,
    b===RETURN_BEAT ? 1 : b>=34 ? .75 : .55,
    "normal", false, b===RETURN_BEAT);
}
for (let b=12; b<FINAL_BEAT; b+=.5) {
  if (b>=BREAK_START && b<RETURN_BEAT) continue;
  if (b>=30 && b<34 && b%1===.5) continue;
  const articulation = b%2===0 ? "kick" : b%1===0 ? "snare" : "hat";
  add("percussion", b, 60, .13, b===RETURN_BEAT ? 1 :
    articulation === "hat" ? .35 : .62, articulation, false,
    b===RETURN_BEAT);
}
for (const [instrumentId,midi] of [
  ["brass",72],["strings",76],["bass",36],["percussion",60],
] as const) add(instrumentId, FINAL_BEAT, midi,
  instrumentId === "percussion" ? .22 : 1.8, 1,
  instrumentId === "percussion" ? "kick" : "normal", true);
export const EVENTS: readonly Note[] = notes.sort((a,b) => a.time-b.time || a.id.localeCompare(b.id));
export const PARTS: Record<InstrumentId, readonly Note[]> = {
  brass: EVENTS.filter(e=>e.instrumentId==="brass"),
  strings: EVENTS.filter(e=>e.instrumentId==="strings"),
  bass: EVENTS.filter(e=>e.instrumentId==="bass"),
  percussion: EVENTS.filter(e=>e.instrumentId==="percussion"),
};
